import { EntityType, useAppSelector } from '@frontend/common';
import { ICONS, iconByType } from '@frontend/icons';
import { useLocale } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { awaitResolve, getResolvedEntity, resolveStore } from '@frontend/repository';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { buildAdminDetailPath } from './admin-detail-path';
import { createAdminObjectBadgeUtils } from './admin-object-badge.utils';

export interface AdminObjectBadgeProps {
    id?: string;
    entityType: EntityType | string;
    display?: string;
    className?: string;
    disableClick?: boolean;
    hideIcon?: boolean;
    /** True when this nested entity belongs to an account other than the requesting user's. */
    foreign?: boolean;
    /** @deprecated Unused on the badge (list share column uses ForeignEntityMarker). Kept for existing call sites. */
    accountId?: string | null;
    /** @deprecated Unused on the badge (list share column uses ForeignEntityMarker). Kept for existing call sites. */
    accountName?: string | null;
    clearable?: {
        onClear: () => void;
    };
    overwriteDisplayMap?: (entity: any) => string;
    styles?: {
        icon?: string;
    };
}

/**
 * Admin-only entity badge. Navigates to the entity detail page (no modal).
 *
 * Label resolution uses the resolve store (same approach as PlatformObjectBadge).
 * The entity is always resolved when an id is present so the detail path can be built
 * (even when `display` overrides the label).
 *
 * Foreign badges use that resolve as a read-rights check: success → clickable; failure → disabled.
 * While resolve is in flight the badge pulses (keeps any provided display name; only shows
 * the loading placeholder when no display label is available yet).
 */
export const AdminObjectBadge = (props: AdminObjectBadgeProps) => {
    const { locale } = useLocale();
    const navigate = useNavigate();
    const { closeAllModals } = useModals();
    const { resolve, mapDisplayName, getNavigateString } = createAdminObjectBadgeUtils(
        props.id || '',
        props.entityType,
        locale,
        props.display,
        props.overwriteDisplayMap
    );
    const resolveState = useAppSelector(useSelector, resolveStore);
    const requestedIdRef = useRef<string | undefined>(undefined);
    const [settledId, setSettledId] = useState<string | undefined>(undefined);
    const isForeign = Boolean(props.foreign);

    const entity = useMemo(() => {
        if (!props.id) {
            return null;
        }
        return getResolvedEntity<{ id: string }>(resolveState, props.entityType as EntityType, props.id);
    }, [props.id, props.entityType, resolveState]);

    const resolveSettled = !props.id || settledId === props.id || Boolean(entity);

    useEffect(() => {
        const id = props.id;
        if (!id || entity || requestedIdRef.current === id) {
            return;
        }
        requestedIdRef.current = id;
        void awaitResolve(id, resolve).then(() => {
            if (requestedIdRef.current === id) {
                setSettledId(id);
            }
        });
    }, [props.id, entity, resolve]);

    const isForeignResolving = isForeign && !!props.id && !entity && !resolveSettled;

    const displayName = useMemo(() => {
        if (props.display !== undefined) {
            return props.display;
        }
        if (isForeignResolving) {
            return undefined;
        }
        if (!props.id) {
            return (
                <FormattedMessage
                    id='badge.none'
                    defaultMessage='None'
                />
            );
        }
        if (entity) {
            return mapDisplayName(entity) ?? props.id;
        }
        if (resolveSettled) {
            return (
                <FormattedMessage
                    id='badge.not_found'
                    defaultMessage='Not found'
                />
            );
        }
        return undefined;
    }, [isForeignResolving, props.display, props.id, entity, resolveSettled, mapDisplayName]);

    const detailPath = useMemo(() => {
        if (!entity) {
            return undefined;
        }
        return buildAdminDetailPath(getNavigateString(entity));
    }, [entity, getNavigateString]);

    const Icon = iconByType(props.entityType as EntityType);
    const isDisabled = !props.id || (isForeign && resolveSettled && !entity) || (!isForeign && !entity && resolveSettled && props.display === undefined);
    const canOpenDetail = Boolean(detailPath);
    const isClickable = !props.disableClick && canOpenDetail && !!props.id && !!entity && !isDisabled;
    const showLoadingLabel = displayName === undefined;

    const styles = {
        default:
            'ignore-modal-close relative px-1 justify-start items-center text-brand-primary text-sm font-semibold rounded-sm inline-flex items-center gap-1 hover:bg-brand-primary hover:text-background-surface hover:cursor-pointer',
        disabled:
            'ignore-modal-close relative px-1 justify-start items-center text-brand-muted text-sm font-semibold rounded-sm inline-flex items-center gap-1 cursor-default'
    };

    const baseBadgeClassName = props.className ? `ignore-modal-close relative ${props.className}` : isClickable ? styles.default : styles.disabled;
    const badgeClassName = isForeignResolving ? `${baseBadgeClassName} animate-pulse` : baseBadgeClassName;

    return (
        <span
            onMouseDown={isClickable ? (e) => e.stopPropagation() : undefined}
            onClick={
                isClickable
                    ? (e) => {
                          e.stopPropagation();
                          if (!detailPath) {
                              return;
                          }
                          closeAllModals();
                          navigate(detailPath);
                      }
                    : undefined
            }
            id={'admin-badge-' + props.id}
            data-testid={props.id ? `admin-badge-${props.id}` : 'admin-badge'}
            className={badgeClassName}>
            {!props.hideIcon &&
                (Icon ? (
                    <Icon className={props.styles?.icon ? props.styles.icon : `size-4 ${isDisabled ? 'text-primary/50' : ''}`} />
                ) : (
                    <span className='placeholder-glow'>
                        <span className='placeholder'>loading</span>
                    </span>
                ))}

            {showLoadingLabel ? (
                <span
                    data-testid='badge-loading-glow'
                    className='placeholder-glow'>
                    <span className='placeholder'>loading</span>
                </span>
            ) : (
                <span className='inline-flex min-w-0 items-center'>{displayName}</span>
            )}
            {props.clearable && (
                <span
                    className='cursor-pointer'
                    onClick={(e) => {
                        e.stopPropagation();
                        props.clearable?.onClear();
                    }}>
                    <ICONS.BUTTON.CLOSE_2 className='text-background-surface size-4' />
                </span>
            )}
        </span>
    );
};
